// src/lib/golfer-dashboard.ts
import { supabase } from '@/integrations/supabase/client';

export type GolferProgress = {
  totalAnalyses: number;
  last30Days: number;
  lastRun?: {
    runId: string;
    createdAt: string;
    flaws: string[]; // flaw_ids from ai_mediapipe_v1_enriched.flaws
  };
};

// ----- Pure helpers (unit-testable) -----

/**
 * Extract flaw IDs from enriched analysis JSON safely
 */
export function extractFlawIds(enrichedJson: any): string[] {
  try {
    const flaws = enrichedJson?.flaws;
    if (!Array.isArray(flaws)) return [];
    
    return flaws
      .map((f: any) => String(f?.flaw_id || '').trim())
      .filter(Boolean);
  } catch (err) {
    console.error('Failed to extract flaw IDs:', err);
    return [];
  }
}

/**
 * Calculate 30-day analysis count from runs
 */
export function count30DayRuns(runs: Array<{ created_at: string }>): number {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  return runs.filter(run => {
    try {
      return new Date(run.created_at) >= thirtyDaysAgo;
    } catch {
      return false;
    }
  }).length;
}

// ----- DB reads (read-only) -----

/**
 * Get golfer's progress summary including total analyses and recent activity
 */
export async function getGolferProgress(userId: string): Promise<GolferProgress> {
  try {
    // Get all completed analysis runs for the user
    const { data: runs, error } = await supabase
      .from('swing_analysis_runs')
      .select('id, created_at, ai_mediapipe_v1_enriched')
      .eq('user_id', userId)
      .eq('status', 'completed')
      .order('created_at', { ascending: false })
      .limit(100); // Cap for performance

    if (error) throw error;

    const allRuns = runs || [];
    const totalAnalyses = allRuns.length;
    const last30Days = count30DayRuns(allRuns);

    // Get the most recent run with flaws for recommendations
    let lastRun: GolferProgress['lastRun'] = undefined;
    for (const run of allRuns) {
      const flaws = extractFlawIds(run.ai_mediapipe_v1_enriched);
      if (flaws.length > 0) {
        lastRun = {
          runId: run.id,
          createdAt: run.created_at,
          flaws,
        };
        break;
      }
    }

    return {
      totalAnalyses,
      last30Days,
      lastRun,
    };
  } catch (err) {
    console.error('getGolferProgress failed:', err);
    return {
      totalAnalyses: 0,
      last30Days: 0,
      lastRun: undefined,
    };
  }
}

